import { useTranslation } from 'react-i18next';
import { Search, TrendingUp, Clock } from 'lucide-react';
import { HelpSearch } from '@/components/help/HelpSearch';
import { HelpCategoryCard } from '@/components/help/HelpCategoryCard';
import { HelpArticleCard } from '@/components/help/HelpArticleCard';
import { useHelpCategories, useHelpArticles, useHelpPopular, useHelpRecent } from '@/hooks/useHelp';

export function HelpCenter() {
  const { t } = useTranslation();
  const categoriesQuery = useHelpCategories();
  const articlesQuery = useHelpArticles();
  const popularQuery = useHelpPopular(6);
  const recentQuery = useHelpRecent(3);

  const categories = categoriesQuery.data ?? [];
  const articles = articlesQuery.data ?? [];
  const popular = popularQuery.data ?? [];
  const recent = recentQuery.data ?? [];

  // Count articles per category
  const articleCounts = articles.reduce(
    (acc, article) => {
      acc[article.category] = (acc[article.category] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      {/* Hero section */}
      <div className="bg-gradient-to-br from-indigo-600 to-indigo-800">
        <div className="mx-auto max-w-7xl px-6 py-16 text-center">
          <h1 className="text-4xl font-bold text-white">
            {t('help.hero_title', 'Hvordan kan vi hjælpe dig?')}
          </h1>
          <p className="mt-3 text-lg text-indigo-100">
            {t('help.hero_subtitle', 'Søg efter svar, guides og tutorials')}
          </p>
          <div className="mx-auto mt-8 max-w-2xl">
            <HelpSearch
              placeholder={t('help.search_placeholder', 'Søg i hjælpecenteret...')}
              autoFocus
            />
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="mx-auto max-w-7xl px-6 py-12">
        {/* Recently viewed */}
        {recent.length > 0 && (
          <section className="mb-12">
            <div className="mb-6 flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-700 dark:text-gray-300" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t('help.recently_viewed', 'Senest set')}
              </h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {recent.map((article) => (
                <HelpArticleCard key={article.id} article={article} showCategory />
              ))}
            </div>
          </section>
        )}

        {/* Categories */}
        <section className="mb-12">
          <div className="mb-6 flex items-center gap-2">
            <Search className="h-5 w-5 text-gray-700 dark:text-gray-300" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('help.browse_categories', 'Gennemse kategorier')}
            </h2>
          </div>

          {categoriesQuery.isLoading ? (
            <div className="py-12 text-center text-gray-500">
              {t('app.loading', 'Indlæser...')}
            </div>
          ) : categories.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-gray-700 dark:bg-card-dark">
              <p className="text-gray-500 dark:text-gray-400">
                {t('help.no_categories', 'Ingen kategorier tilgængelige')}
              </p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {categories.map((category) => (
                <HelpCategoryCard
                  key={category.id}
                  category={category}
                  articleCount={articleCounts[category.id]}
                />
              ))}
            </div>
          )}
        </section>

        {/* Popular articles */}
        {popular.length > 0 && (
          <section>
            <div className="mb-6 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-gray-700 dark:text-gray-300" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t('help.popular_articles', 'Populære artikler')}
              </h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {popular.map((article) => (
                <HelpArticleCard key={article.id} article={article} showCategory />
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
